import React, { ReactNode, createContext, useContext, useMemo } from 'react';

import useLocalStorage from '@/operator/hooks/usePersistState';

import { OrgListContextDefaultValues, OrgListContextType } from './org';
import {
    PreviousURLContextDefaultValues,
    PreviousURLContextType,
} from './previousURL';
import {
    TenantProvisioningContextDefaultValues,
    TenantProvisioningContextType,
} from './tenantProvisioning';
import { TOSContextDefaultValues, TOSContextType } from './tos';
import { UserContextDefaultValues, UserContextType } from './user';
import { UserOrg } from 'graphql/types/userOrgs';

// Define the type for the combined context
type ContextType = TOSContextType &
    TenantProvisioningContextType &
    UserContextType &
    PreviousURLContextType &
    OrgListContextType;

// Create the default values for the combined context
const ContextDefaultValues: ContextType = {
    ...TOSContextDefaultValues,
    ...TenantProvisioningContextDefaultValues,
    ...PreviousURLContextDefaultValues,
    ...UserContextDefaultValues,
    ...OrgListContextDefaultValues,
};

const AppContext = createContext<ContextType>(ContextDefaultValues);

export const useAppContext = () => useContext(AppContext);

type AppContextProviderProps = {
    children: ReactNode;
};

// AppContextProvider component
export const AppContextProvider = ({ children }: AppContextProviderProps) => {
    // TOS using useLocalStorage hook
    const [isTOSEnabled, setIsTOSEnabled] = useLocalStorage<boolean>(
        'isTOSEnabled',
        false,
    );

    const enableTOS = () => setIsTOSEnabled(true);
    const disableTOS = () => setIsTOSEnabled(false);

    // Tenant Provisioning using useLocalStorage hook
    const [businessName, setBusinessName] = useLocalStorage<string>(
        'businessName',
        '',
    );
    const [vanityURL, setVanityURL] = useLocalStorage<string>('vanityURL', '');

    // Previous URL recognition using useLocalStorage hook
    const [previousURL, setPreviousURL] = useLocalStorage<string>(
        'previousURL',
        '',
    );

    const [givenName, setGivenName] = useLocalStorage<string>('givenName', '');

    const [orgList, setOrgList] = useLocalStorage<UserOrg[]>('', []);
    const [surName, setSurname] = useLocalStorage<string>('surName', '');
    const [preferredName, setPreferredName] = useLocalStorage<string>(
        'preferredName',
        '',
    );
    const [pronouns, setPronouns] = useLocalStorage<string>('pronouns', '');
    const [avatar, setAvatar] = useLocalStorage<string>('avatar', '');
    const [avatarName, setAvatarName] = useLocalStorage<string>(
        'avatarName',
        '',
    );

    // General
    const contextStateValue: ContextType = useMemo(
        () => ({
            // TOS
            isTOSEnabled,
            enableTOS,
            disableTOS,
            // Tenant Provisioning
            businessName,
            setBusinessName,
            vanityURL,
            setVanityURL,
            // Previous URL recognition
            previousURL,
            setPreviousURL,
            givenName,
            setGivenName,
            surName,
            setSurname,
            preferredName,
            setPreferredName,
            pronouns,
            setPronouns,
            avatar,
            setAvatar,
            avatarName,
            setAvatarName,
            orgList,
            setOrgList,
        }),
        [
            isTOSEnabled,
            disableTOS,
            businessName,
            setBusinessName,
            vanityURL,
            setVanityURL,
            previousURL,
            setPreviousURL,
            givenName,
            setGivenName,
            surName,
            setSurname,
            preferredName,
            setPreferredName,
            pronouns,
            setPronouns,
            avatar,
            setAvatar,
            avatarName,
            setAvatarName,
            orgList,
            setOrgList,
        ],
    );

    return (
        <AppContext.Provider value={contextStateValue}>
            {children}
        </AppContext.Provider>
    );
};
